import PropTypes from 'prop-types';
import React, { useState } from 'react';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { ToggleButton } from 'primereact/togglebutton';
import _ from 'lodash';
import { useMutation } from '@apollo/react-hooks';
import { injectIntl } from 'react-intl';
import { ScrollPanel } from 'primereact/scrollpanel';
import { UPDATE_GLOBAL_ARI } from '../mutations';
import messages from '../intl-messages';
import { withGrowl } from '../../../../components/GrowlNotifications';
import './enforced-styles.css';


const EnforcedOnAccounts = ({
  field, currentAccount, intl: { formatMessage }, growl,
}) => {
  const [updateGlobalAri, updateGlobalAriResult] = useMutation(
    UPDATE_GLOBAL_ARI, { refetchQueries: ['getAccount'] },
  );

  const initialSelected = [
    ...field.availableAccountsToEnable.map(acc => ({
      value: acc.id,
      label: acc.displayName,
      selected: !!_.find(field.accountsWithFieldEnabled, account => account.id === acc.id),
    }))];

  const [modalOpen, setModalOpen] = useState(false);
  const [choices, setChoices] = useState(initialSelected);

  const changesMade = () => !_.isEqual(initialSelected, choices);
  const noneSelected = () => _.every(choices, choice => choice.selected === false);
  const allSelected = () => _.every(choices, choice => choice.selected === true);

  return (
    <>
      <div>
        <small>
          {formatMessage(messages.manageEnforceLabel)}
        </small>
      </div>
      <Button
        disabled={updateGlobalAriResult.loading}
        type="button"
        className="p-button-secondary"
        label="..."
        onClick={() => {
          setChoices(initialSelected);
          setModalOpen(true);
        }}
      />

      <Dialog
        style={{ maxWidth: '75vw', maxHeight: '75vh' }}
        header={formatMessage(messages.globalManageEnforcedDialogHeader)}
        visible={modalOpen}
        modal
        onHide={() => {
          if (changesMade()) {
            setChoices(initialSelected);
          }
          setModalOpen(false);
        }}
      >
        <ScrollPanel style={{
          width: '100%',
          height: '400px',
          marginBottom: '10px',
        }}
        >
          <div className="p-grid p-justify-center" style={{ padding: '5px' }}>
            {
            choices.map((acc) => {
              const current = _.find(choices, cc => cc.value === acc.value);

              return (
                <ToggleButton
                  className="enforce-account"
                  key={acc.value}
                  checked={current.selected}
                  onChange={(e) => {
                    setChoices(choices.map(
                      choice => (choice.value === current.value
                        ? Object.assign({}, choice, { selected: e.value })
                        : choice),
                    ));
                  }}
                  onLabel={acc.label}
                  offLabel={acc.label}
                  onIcon="pi pi-check"
                  offIcon="pi pi-times"
                />
              );
            })
          }
          </div>
        </ScrollPanel>
        <div>
          <Button
            icon="pi pi-check"
            className="p-button-secondary"
            label={formatMessage(messages.enforceAllLabel)}
            iconPos="left"
            disabled={allSelected()}
            onClick={() => { setChoices(choices.map(choice => Object.assign({}, choice, { selected: true }))); }}
          />
          <Button
            icon="pi pi-check"
            className="p-button-secondary"
            label={formatMessage(messages.enforceNoneLabel)}
            iconPos="left"
            disabled={noneSelected()}
            onClick={() => { setChoices(choices.map(choice => Object.assign({}, choice, { selected: false }))); }}
          />
          <br />
          <Button
            icon="pi pi-check"
            className="p-button-secondary"
            label={formatMessage(messages.enforceResetLabel)}
            iconPos="left"
            disabled={!changesMade()}
            onClick={() => { setChoices(initialSelected); }}
            style={{ marginTop: '5px' }}
          />
          <br />
          <Button
            label={formatMessage(messages.submitLabel)}
            disabled={!changesMade()}
            className="p-button-warning"
            style={{ marginTop: '5px' }}
            onClick={() => {
              const input = {
                accountId: currentAccount,
                ariFieldId: field.id,
                dataType: field.dataType,
                question: field.question,
                answers: field.answers,
                mandatory: field.mandatory,
                availableAccountsToEnable:
                _.filter(choices, choice => choice.selected === true)
                  .map(choice => choice.value),
              };

              updateGlobalAri({ variables: { input } })
                .then(() => {
                  growl.show({
                    severity: 'success',
                    sticky: false,
                    summary: formatMessage(messages.growlAddFieldSuccessSummary),
                    detail: formatMessage(messages.growlAccountEnforceSuccessDetail),
                  });
                })
                .catch(() => {
                  growl.show({
                    severity: 'error',
                    sticky: true,
                    summary: formatMessage(messages.growlAddFieldFailSummary),
                    detail: formatMessage(messages.growlAccountEnforceFailDetail),
                  });
                });

              setModalOpen(false);
            }}
          />
        </div>
      </Dialog>
    </>
  );
};

EnforcedOnAccounts.propTypes = {
  currentAccount: PropTypes.string.isRequired,
  field: PropTypes.objectOf(PropTypes.any).isRequired,
  intl: PropTypes.objectOf(PropTypes.any).isRequired,
  growl: PropTypes.objectOf(PropTypes.any).isRequired,
};

export default injectIntl(withGrowl(EnforcedOnAccounts));
